import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, API } from "@/App";
import { 
  ArrowLeft, Navigation, MapPin, Clock, CheckCircle2, 
  SkipForward, X, Loader2, ChevronRight, 
  TrendingUp, CornerUpRight, CornerUpLeft, ArrowUp, RotateCcw, Navigation2,
  Camera, Star, Cloud, Trophy, Route, Beer
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { WeatherCard } from "@/components/WeatherCard";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(window.google);
      return;
    }
    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google));
      return;
    }
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const getDirectionIcon = (maneuver) => {
  if (!maneuver) return <ArrowUp className="w-8 h-8" />;
  if (maneuver.includes('left')) return <CornerUpLeft className="w-8 h-8" />;
  if (maneuver.includes('right')) return <CornerUpRight className="w-8 h-8" />;
  if (maneuver.includes('uturn')) return <RotateCcw className="w-8 h-8" />;
  if (maneuver.includes('merge')) return <TrendingUp className="w-8 h-8" />;
  return <ArrowUp className="w-8 h-8" />;
};

const ActiveRide = () => {
  const navigate = useNavigate();
  const { tripId } = useParams();
  const { user } = useAuth();
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const userMarkerRef = useRef(null);
  const watchIdRef = useRef(null);
  const directionsRendererRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [gettingLocation, setGettingLocation] = useState(true);
  const [activeRide, setActiveRide] = useState(null);
  const [trip, setTrip] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInPhotos, setCheckInPhotos] = useState([]);
  const [checkInReview, setCheckInReview] = useState("");
  const [checkInRating, setCheckInRating] = useState(5);
  const [locationError, setLocationError] = useState(null);
  const [manualLocationMode, setManualLocationMode] = useState(false);
  const [manualAddress, setManualAddress] = useState("");
  const [allStopsComplete, setAllStopsComplete] = useState(false);
  
  // Navigation state
  const [directions, setDirections] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [distanceToNext, setDistanceToNext] = useState(null);
  const [durationToNext, setDurationToNext] = useState(null);
  const [navigationStarted, setNavigationStarted] = useState(false);
  const [showWeather, setShowWeather] = useState(false);
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [completionStats, setCompletionStats] = useState(null);
  const [completing, setCompleting] = useState(false);

  // Function to request location
  const requestLocation = () => {
    setGettingLocation(true);
    setLocationError(null);
    
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        console.log("Location obtained:", loc);
        setCurrentLocation(loc);
        setGettingLocation(false);
        setLocationError(null);
      },
      (error) => {
        console.error("Location error:", error);
        let errorMessage = "Could not get your location.";
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission denied. Please allow location access in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable. Please try again.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            break;
          default:
            errorMessage = "An unknown error occurred getting your location.";
        }
        
        setLocationError(errorMessage);
        setGettingLocation(false);
      },
      { 
        enableHighAccuracy: true, 
        timeout: 20000,
        maximumAge: 0 
      }
    );
  };

  // Geocode an address to coordinates
  const geocodeAddress = async (address) => {
    try {
      await loadGoogleMapsScript();
      const geocoder = new window.google.maps.Geocoder();
      
      return new Promise((resolve, reject) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const loc = {
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng()
            };
            resolve(loc);
          } else {
            reject(new Error('Could not find that address'));
          }
        });
      });
    } catch (error) {
      throw error;
    }
  };

  const handleManualLocation = async () => {
    if (!manualAddress.trim()) {
      toast.error("Please enter an address");
      return;
    }
    
    setGettingLocation(true);
    try {
      const loc = await geocodeAddress(manualAddress);
      setCurrentLocation(loc);
      setLocationError(null);
      setManualLocationMode(false);
      toast.success("Starting location set!");
    } catch (error) {
      toast.error("Could not find that address. Try a more specific address.");
    } finally {
      setGettingLocation(false);
    }
  };

  const useTestLocation = () => {
    // Use first stop location as starting point for testing
    if (trip?.stops?.[0]?.coordinates) {
      const firstStop = trip.stops[0].coordinates;
      // Offset slightly from first stop
      setCurrentLocation({
        lat: firstStop.lat + 0.01,
        lng: firstStop.lng + 0.01
      });
      setLocationError(null);
      toast.success("Using test location near your first stop");
    } else {
      // Default to San Francisco
      setCurrentLocation({ lat: 37.7749, lng: -122.4194 });
      setLocationError(null);
      toast.success("Using San Francisco as test location");
    }
  };

  // Get current location on mount
  useEffect(() => {
    requestLocation();
  }, []);

  // Fetch trip data
  useEffect(() => {
    const fetchTrip = async () => {
      if (!tripId) {
        navigate('/dashboard');
        return;
      }
      
      try {
        const tripResponse = await axios.get(`${API}/trips/${tripId}`);
        setTrip(tripResponse.data);
      } catch (error) {
        toast.error("Failed to load trip");
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchTrip();
  }, [tripId]);

  // Initialize map
  useEffect(() => {
    if (!trip || !currentLocation || !mapRef.current) return;

    const initMap = async () => {
      try {
        await loadGoogleMapsScript();
        
        if (mapInstanceRef.current) return;

        const mapStyles = [
          { elementType: "geometry", stylers: [{ color: "#1d1d1d" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#1d1d1d" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c2c2c" }] },
          { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3c3c3c" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e0e0e" }] },
        ];

        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: currentLocation,
          zoom: 14,
          styles: mapStyles,
          disableDefaultUI: true,
          zoomControl: true,
        });

        // Add user marker
        userMarkerRef.current = new window.google.maps.Marker({
          position: currentLocation,
          map: mapInstanceRef.current,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: "#00FFFF",
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 3,
          },
          title: "You",
        });

        // Add stop markers
        trip.stops?.forEach((stop, index) => {
          if (!stop.coordinates?.lat) return;
          
          const isCurrent = index === currentStopIndex;

          new window.google.maps.Marker({
            position: { lat: stop.coordinates.lat, lng: stop.coordinates.lng },
            map: mapInstanceRef.current,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: isCurrent ? 14 : 10,
              fillColor: isCurrent ? "#CD7F32" : "#666",
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 2,
            },
            label: {
              text: String(index + 1),
              color: "#fff",
              fontSize: "12px",
              fontWeight: "bold"
            },
            title: stop.name,
          });
        });

        // Initialize directions renderer
        directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
          map: mapInstanceRef.current,
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: "#CD7F32",
            strokeOpacity: 0.9,
            strokeWeight: 5,
          }
        });

      } catch (error) {
        console.error("Map init error:", error);
      }
    };

    initMap();
  }, [trip, currentLocation]);

  // Watch user location continuously
  useEffect(() => {
    if (!navigator.geolocation || !navigationStarted) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setCurrentLocation(newLocation);

        if (userMarkerRef.current) {
          userMarkerRef.current.setPosition(newLocation);
        }

        if (mapInstanceRef.current && navigationStarted) {
          mapInstanceRef.current.panTo(newLocation);
        }
      },
      (error) => console.error("Location watch error:", error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 3000 }
    );

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [navigationStarted]);

  // Get directions to next stop (for in-app display)
  const getDirections = async () => {
    if (!currentLocation || !trip?.stops?.length) return;

    const currentStop = trip.stops[currentStopIndex];
    if (!currentStop?.coordinates) return;

    try {
      const directionsService = new window.google.maps.DirectionsService();
      
      const result = await directionsService.route({
        origin: currentLocation,
        destination: { lat: currentStop.coordinates.lat, lng: currentStop.coordinates.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
      });

      if (result.status === 'OK') {
        setDirections(result);
        
        if (directionsRendererRef.current) {
          directionsRendererRef.current.setDirections(result);
        }

        const leg = result.routes[0].legs[0];
        setDistanceToNext(leg.distance.text);
        setDurationToNext(leg.duration.text);
        setCurrentStep(0);
        setNavigationStarted(true);

        // Start ride in backend
        if (!activeRide) {
          try {
            const startResponse = await axios.post(`${API}/rides/start`, { trip_id: tripId });
            setActiveRide(startResponse.data);
          } catch (error) {
            console.error("Failed to start ride:", error);
          }
        }
      }
    } catch (error) {
      console.error("Directions error:", error);
      toast.error("Could not get directions. Check your internet connection.");
    }
  };

  // Open Google Maps for turn-by-turn navigation
  const openGoogleMapsNavigation = () => {
    if (!trip?.stops?.length) return;

    const currentStop = trip.stops[currentStopIndex];
    if (!currentStop?.coordinates) {
      toast.error("No destination coordinates available");
      return;
    }

    const { lat, lng } = currentStop.coordinates;
    
    // Build Google Maps URL with destination
    // Using the directions URL format that opens navigation mode
    let mapsUrl;
    
    // If we have current location, include it as origin
    if (currentLocation) {
      mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${currentLocation.lat},${currentLocation.lng}&destination=${lat},${lng}&travelmode=driving`;
    } else {
      // Just destination - Google Maps will use user's current location
      mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    }

    // Open Google Maps in a new tab/app
    window.open(mapsUrl, '_blank');
    
    // Mark navigation as started in our app
    if (!navigationStarted) {
      setNavigationStarted(true);
      
      // Start ride in backend if not already started
      if (!activeRide) {
        axios.post(`${API}/rides/start`, { trip_id: tripId })
          .then(response => setActiveRide(response.data))
          .catch(error => console.error("Failed to start ride:", error));
      }
      
      // Get directions for in-app display
      getDirections();
    }
    
    toast.success("Opening Google Maps...");
  };

  const currentStop = trip?.stops?.[currentStopIndex];
  const totalStops = trip?.stops?.length || 0;
  const currentDirectionStep = directions?.routes?.[0]?.legs?.[0]?.steps?.[currentStep];

  const handleCheckIn = async () => {
    if (!currentLocation || !currentStop) {
      toast.error("Cannot verify location");
      return;
    }

    setCheckingIn(true);
    try {
      // Check in to the stop
      const response = await axios.post(`${API}/checkins`, {
        stop_id: currentStop.stop_id,
        trip_id: trip.trip_id,
        lat: currentLocation.lat,
        lng: currentLocation.lng,
        photos: checkInPhotos
      });

      // Submit review if provided
      if (checkInReview.trim() || checkInRating) {
        try {
          await axios.post(`${API}/reviews`, {
            stop_id: currentStop.stop_id,
            rating: checkInRating,
            comment: checkInReview
          });
        } catch (e) {
          console.log("Review submission failed", e);
        }
      }

      if (response.data.verified) {
        toast.success("Checked in! 🎉");
      } else {
        toast.warning(response.data.message);
      }

      // Reset check-in form
      setCheckInPhotos([]);
      setCheckInReview("");
      setCheckInRating(5);
      setCheckInModalOpen(false);

      // Check if this was the last stop
      if (currentStopIndex >= totalStops - 1) {
        // All stops complete - show completion screen
        setAllStopsComplete(true);
      } else {
        // Move to next stop
        setCurrentStopIndex(prev => prev + 1);
        setDirections(null);
        setNavigationStarted(false);
      }

    } catch (error) {
      toast.error("Check-in failed");
    } finally {
      setCheckingIn(false);
    }
  };

  const handleSkipStop = () => {
    if (currentStopIndex < totalStops - 1) {
      setCurrentStopIndex(prev => prev + 1);
      setDirections(null);
      setNavigationStarted(false);
      toast("Stop skipped - getting new directions...");
    }
  };

  const handleCompleteRide = async () => {
    setCompleting(true);
    try {
      const response = await axios.post(`${API}/rides/complete`);
      setCompletionStats(response.data);
      setCompletionModalOpen(true);
    } catch (error) {
      toast.error("Failed to complete ride");
      setCompleting(false);
    }
  };

  const handleCompletionDone = () => {
    setCompletionModalOpen(false);
    navigate('/dashboard');
  };

  const handleCancelRide = async () => {
    if (!window.confirm("Cancel this ride?")) return;
    
    try {
      await axios.post(`${API}/rides/cancel`);
      navigate('/dashboard');
    } catch (error) {
      navigate('/dashboard');
    }
  };

  const getCategoryEmoji = (category) => {
    switch (category) {
      case 'dive_bar': return '🍺';
      case 'restaurant': return '🍔';
      case 'landmark': return '🗿';
      case 'hotel': return '🏨';
      case 'gas_station': return '⛽';
      case 'rest_stop': return '☕';
      default: return '📍';
    }
  };

  if (loading || gettingLocation) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center asphalt-texture">
        <div className="text-center glass rounded-xl p-8 mx-4">
          <Loader2 className="w-12 h-12 text-bronze mx-auto animate-spin" />
          <p className="text-gray-300 mt-4 font-medium">
            {gettingLocation ? "Getting your location..." : "Loading trip..."}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Please allow location access when prompted
          </p>
        </div>
      </div>
    );
  }

  if ((!currentLocation || locationError) && !manualLocationMode) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center asphalt-texture p-4">
        <div className="text-center glass rounded-xl p-6 mx-4 max-w-sm">
          <MapPin className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="font-oswald text-xl mt-4">Location Required</h2>
          <p className="text-gray-400 text-sm mt-2 mb-4">
            {locationError || "Could not get your location automatically."}
          </p>
          
          <div className="space-y-3">
            <Button
              className="w-full bg-bronze hover:bg-bronze-600"
              onClick={requestLocation}
            >
              <MapPin className="w-4 h-4 mr-2" />
              Try Auto-Detect Again
            </Button>
            
            <Button
              variant="outline"
              className="w-full border-white/20"
              onClick={() => setManualLocationMode(true)}
            >
              <Navigation className="w-4 h-4 mr-2" />
              Enter Starting Address
            </Button>
            
            <Button
              variant="ghost"
              className="w-full text-cyan-400 hover:text-cyan-300"
              onClick={useTestLocation}
            >
              Use Test Location
            </Button>
            
            <Button
              variant="ghost"
              className="w-full text-gray-500"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (manualLocationMode) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center asphalt-texture p-4">
        <div className="glass rounded-xl p-6 mx-4 max-w-sm w-full">
          <h2 className="font-oswald text-xl mb-4">Enter Starting Location</h2>
          <p className="text-gray-400 text-sm mb-4">
            Enter your current address or a nearby landmark
          </p>
          
          <input
            type="text"
            value={manualAddress}
            onChange={(e) => setManualAddress(e.target.value)}
            placeholder="123 Main St, City, State"
            className="w-full h-12 px-4 bg-[#121212] border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:border-bronze focus:outline-none mb-4"
            onKeyDown={(e) => e.key === 'Enter' && handleManualLocation()}
          />
          
          <div className="space-y-3">
            <Button
              className="w-full bg-bronze hover:bg-bronze-600"
              onClick={handleManualLocation}
            >
              <Navigation className="w-4 h-4 mr-2" />
              Set Starting Point
            </Button>
            
            <Button
              variant="ghost"
              className="w-full text-gray-500"
              onClick={() => setManualLocationMode(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentLocation) {
    return null; // Should not reach here
  }

  return (
    <div className="mobile-container asphalt-texture min-h-screen flex flex-col">
      {/* Map */}
      <div className="relative h-[40vh]">
        <div ref={mapRef} className="w-full h-full" />
        
        {/* Header Overlay */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          <Button
            variant="ghost"
            size="icon"
            className="glass rounded-full w-10 h-10"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className={`glass rounded-full w-10 h-10 ${showWeather ? 'text-cyan-400' : ''}`}
              onClick={() => setShowWeather(!showWeather)}
            >
              <Cloud className="w-5 h-5" />
            </Button>
            <div className="glass rounded-lg px-3 py-2 text-center">
              <div className="text-xs text-gray-400 uppercase">Stop</div>
              <div className="font-oswald font-bold text-bronze">{currentStopIndex + 1}/{totalStops}</div>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="glass rounded-full w-10 h-10 text-red-500"
            onClick={handleCancelRide}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Weather Overlay */}
        {showWeather && currentStop?.coordinates?.lat && (
          <div className="absolute bottom-4 left-4 right-4">
            <WeatherCard 
              lat={currentStop.coordinates.lat} 
              lng={currentStop.coordinates.lng}
              className="shadow-lg"
            />
          </div>
        )}
      </div>

      {/* Navigation Card */}
      {navigationStarted && currentDirectionStep ? (
        <div className="bg-bronze p-4">
          <div className="flex items-center gap-4">
            <div className="text-white">
              {getDirectionIcon(currentDirectionStep.maneuver)}
            </div>
            <div className="flex-1">
              <p 
                className="text-white font-medium text-lg"
                dangerouslySetInnerHTML={{ __html: currentDirectionStep.instructions }}
              />
              <p className="text-white/70 text-sm mt-1">
                {currentDirectionStep.distance?.text} • {currentDirectionStep.duration?.text}
              </p>
            </div>
          </div>
          
          {/* Step navigation */}
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              variant="ghost"
              className="text-white/80 hover:text-white hover:bg-white/10"
              onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
              disabled={currentStep === 0}
            >
              Prev
            </Button>
            <div className="flex-1 text-center text-white/60 text-sm">
              Step {currentStep + 1} of {directions?.routes?.[0]?.legs?.[0]?.steps?.length}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-white/80 hover:text-white hover:bg-white/10"
              onClick={() => setCurrentStep(prev => Math.min(directions?.routes?.[0]?.legs?.[0]?.steps?.length - 1, prev + 1))}
              disabled={currentStep >= (directions?.routes?.[0]?.legs?.[0]?.steps?.length || 1) - 1}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      {/* Current Stop Info */}
      <div className="flex-1 p-4 overflow-auto">
        {currentStop && !allStopsComplete ? (
          <>
            {/* Check if current stop is End of Day */}
            {currentStop.estimated_time === -1 ? (
              <div className="glass rounded-xl p-4 mb-4 border-purple-500/30 bg-purple-500/5">
                {/* Distance/Time to End of Day stop */}
                {navigationStarted && distanceToNext && (
                  <div className="flex justify-center gap-6 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-oswald font-bold text-purple-400">{distanceToNext}</div>
                      <div className="text-xs text-gray-500 uppercase">Distance</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-oswald font-bold text-cyan-400">{durationToNext}</div>
                      <div className="text-xs text-gray-500 uppercase">ETA</div>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-4">
                  <div className="text-3xl">🌙</div>
                  <div className="flex-1">
                    <div className="text-xs text-purple-400 uppercase tracking-wide mb-1">
                      {navigationStarted ? 'Heading to' : 'Next Stop'} • End of Day
                    </div>
                    <h2 className="font-oswald text-xl font-bold text-purple-300">{currentStop.name}</h2>
                    {currentStop.address && (
                      <p className="text-sm text-gray-400 mt-1">{currentStop.address}</p>
                    )}
                    {!navigationStarted && (
                      <p className="text-sm text-purple-400/70 mt-2">Get directions, then check in for the night</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  {!navigationStarted ? (
                    <Button
                      className="flex-1 h-14 bg-purple-600 hover:bg-purple-700 font-oswald uppercase tracking-wider text-lg"
                      onClick={openGoogleMapsNavigation}
                    >
                      <Navigation2 className="w-5 h-5 mr-2" />
                      Navigate to Stay
                    </Button>
                  ) : (
                    <>
                      <Button
                        className="flex-1 h-12 bg-cyan-600 hover:bg-cyan-700 font-oswald uppercase"
                        onClick={openGoogleMapsNavigation}
                      >
                        <Navigation2 className="w-5 h-5 mr-2" />
                        Open Maps
                      </Button>
                      <Button
                        className="flex-1 h-12 bg-purple-600 hover:bg-purple-700 font-oswald uppercase"
                        onClick={() => setCheckInModalOpen(true)}
                      >
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                        Check In
                      </Button>
                      <Button
                        variant="outline"
                        className="h-12 border-white/20 px-3"
                        onClick={handleSkipStop}
                      >
                        <SkipForward className="w-5 h-5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Distance/Time to stop */}
                {distanceToNext && (
                  <div className="flex justify-center gap-6 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-oswald font-bold text-bronze">{distanceToNext}</div>
                      <div className="text-xs text-gray-500 uppercase">Distance</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-oswald font-bold text-cyan-400">{durationToNext}</div>
                      <div className="text-xs text-gray-500 uppercase">ETA</div>
                    </div>
                  </div>
                )}

                <div className="glass rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">{getCategoryEmoji(currentStop.category)}</div>
                    <div className="flex-1">
                      <div className="text-xs text-cyan-400 uppercase tracking-wide mb-1">
                        {navigationStarted ? 'Heading to' : 'Next Stop'}
                      </div>
                      <h2 className="font-oswald text-xl font-bold">{currentStop.name}</h2>
                      {currentStop.address && (
                        <p className="text-sm text-gray-400 mt-1">{currentStop.address}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    {!navigationStarted ? (
                      <Button
                        className="flex-1 h-14 bg-bronze hover:bg-bronze-600 font-oswald uppercase tracking-wider text-lg"
                        onClick={openGoogleMapsNavigation}
                      >
                        <Navigation2 className="w-5 h-5 mr-2" />
                        Navigate in Maps
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="flex-1 h-12 bg-cyan-600 hover:bg-cyan-700 font-oswald uppercase"
                          onClick={openGoogleMapsNavigation}
                        >
                          <Navigation2 className="w-5 h-5 mr-2" />
                          Open Maps
                        </Button>
                        <Button
                          className="flex-1 h-12 bg-green-600 hover:bg-green-700 font-oswald uppercase"
                          onClick={() => setCheckInModalOpen(true)}
                        >
                          <CheckCircle2 className="w-5 h-5 mr-2" />
                          I'm Here
                        </Button>
                        <Button
                          variant="outline"
                          className="h-12 border-white/20 px-4"
                          onClick={handleSkipStop}
                        >
                          <SkipForward className="w-4 h-4 mr-1" />
                          Skip
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Upcoming Stops */}
            {trip?.stops && currentStopIndex < totalStops - 1 && (
              <div>
                <h3 className="text-xs text-gray-500 uppercase tracking-wide mb-2">Coming Up</h3>
                <div className="space-y-2">
                  {trip.stops.slice(currentStopIndex + 1, currentStopIndex + 3).map((stop, idx) => (
                    <div 
                      key={stop.stop_id} 
                      className={`glass rounded-lg p-3 flex items-center gap-3 ${
                        stop.estimated_time === -1 ? 'border-purple-500/30 bg-purple-500/5' : ''
                      }`}
                    >
                      <span className="text-lg">
                        {stop.estimated_time === -1 ? '🌙' : getCategoryEmoji(stop.category)}
                      </span>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${stop.estimated_time === -1 ? 'text-purple-300' : ''}`}>
                          {stop.name}
                        </p>
                        {stop.estimated_time === -1 && (
                          <p className="text-xs text-purple-400/70">End of Day</p>
                        )}
                      </div>
                      <ChevronRight className={`w-4 h-4 ${stop.estimated_time === -1 ? 'text-purple-500' : 'text-gray-600'}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="glass rounded-xl p-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h2 className="font-oswald text-xl font-bold uppercase mb-2">All Stops Complete!</h2>
            <p className="text-gray-400 text-sm mb-4">You've visited all {totalStops} stops.</p>
            <Button
              className="w-full h-12 bg-green-500 hover:bg-green-600 font-oswald uppercase"
              onClick={handleCompleteRide}
              disabled={completing}
            >
              {completing ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : null}
              Complete Ride 🏁
            </Button>
          </div>
        )}
      </div>

      {/* Check-In Modal */}
      <Dialog open={checkInModalOpen} onOpenChange={setCheckInModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4 max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-oswald uppercase flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Check In
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-3 py-2">
            {/* Stop Info - Compact */}
            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
              <div className="text-2xl">{getCategoryEmoji(currentStop?.category)}</div>
              <div className="flex-1 min-w-0">
                <h3 className="font-oswald font-bold truncate">{currentStop?.name}</h3>
                <div className="flex items-center gap-1 text-green-500 text-xs">
                  <MapPin className="w-3 h-3" />
                  <span>Location verified</span>
                </div>
              </div>
            </div>

            {/* Rating - Compact */}
            <div className="space-y-1">
              <label className="text-xs text-gray-400 uppercase">Rate this spot</label>
              <div className="flex justify-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setCheckInRating(s)}
                    className="p-0.5"
                  >
                    <Star
                      className={`w-7 h-7 transition-colors ${
                        s <= checkInRating 
                          ? 'text-yellow-500 fill-yellow-500' 
                          : 'text-gray-600'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Review - Compact */}
            <div className="space-y-1">
              <label className="text-xs text-gray-400 uppercase">Quick Review (optional)</label>
              <Textarea
                value={checkInReview}
                onChange={(e) => setCheckInReview(e.target.value)}
                placeholder="Great vibes, cold beers..."
                className="bg-[#121212] border-white/10 min-h-[50px] max-h-[80px] text-sm"
              />
            </div>

            {/* Photo Upload - Compact */}
            <div className="space-y-1">
              <label className="text-xs text-gray-400 uppercase flex items-center gap-1">
                <Camera className="w-3 h-3" />
                Add Photo (optional)
              </label>
              <ImageUpload
                images={checkInPhotos}
                onImagesChange={setCheckInPhotos}
                maxImages={1}
                folder="checkins"
              />
            </div>
          </div>

          {/* Fixed Button at Bottom */}
          <div className="flex-shrink-0 pt-2 border-t border-white/10">
            <Button
              className="w-full h-12 bg-bronze hover:bg-bronze-600 font-oswald uppercase"
              onClick={handleCheckIn}
              disabled={checkingIn}
            >
              {checkingIn ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  Confirm Check-In
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Completion Modal */}
      <Dialog open={completionModalOpen} onOpenChange={() => {}}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4">
          <div className="text-center py-4">
            <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-500/30">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mb-2 text-white">
              Ride Complete!
            </h2>
            <p className="text-gray-400 mb-6">Great job finishing your adventure!</p>
            
            {completionStats && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="glass rounded-xl p-4 text-center">
                  <Route className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <div className="text-2xl font-oswald font-bold text-white">
                    {completionStats.miles_logged}
                  </div>
                  <div className="text-xs text-gray-500 uppercase">Miles</div>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <Clock className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                  <div className="text-2xl font-oswald font-bold text-white">
                    {completionStats.duration_hours < 1 
                      ? `${Math.round(completionStats.duration_hours * 60)}m`
                      : `${completionStats.duration_hours.toFixed(1)}h`
                    }
                  </div>
                  <div className="text-xs text-gray-500 uppercase">Duration</div>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <MapPin className="w-6 h-6 text-bronze mx-auto mb-2" />
                  <div className="text-2xl font-oswald font-bold text-white">
                    {completionStats.stops_completed}
                  </div>
                  <div className="text-xs text-gray-500 uppercase">Stops</div>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <Beer className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                  <div className="text-2xl font-oswald font-bold text-white">
                    {completionStats.bars_visited}
                  </div>
                  <div className="text-xs text-gray-500 uppercase">Dives</div>
                </div>
              </div>
            )}
            
            <p className="text-sm text-gray-500 mb-4">
              Your stats have been added to the leaderboard!
            </p>
            
            <Button
              className="w-full h-12 bg-bronze hover:bg-bronze-600 font-oswald uppercase tracking-wider"
              onClick={handleCompletionDone}
            >
              Back to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActiveRide;
